import { baseCustomer, day, emptyPlan, withPaidPrices } from './factory'
import { shiftMonths } from '../../core/format/dates'
import type { Account } from '../../core/types/account'

/**
 * DEMO — alle voorbeeldklanten, gedeeld door Mijn omgeving (/account) en het
 * beheer (/be/klanten). Elke klant laat een andere situatie zien. Met een
 * backend komt dit van GET /account en GET /admin/customers, en verdwijnt de
 * hele map demo/.
 */

/** Wachtwoord voor alle demoklanten. */
export const DEMO_PASSWORD = 'huid'

/** Voorbeeldfoto voor het fotodagboek als er geen camera is (via accountApi.demoSamplePhoto). */
export const demoDiaryUpload = 'https://cdn.magicpatterns.com/patterns/generated-images/f30c29fc-b487-42b0-8a2b-c85c4cb56e64.jpg'

const photo = {
  a: 'https://cdn.magicpatterns.com/patterns/generated-images/45aa10d6-44fe-4c78-978e-ba64c6bc5c13.jpg',
  b: 'https://cdn.magicpatterns.com/patterns/generated-images/87b523c7-2723-4963-bc7e-32230013af17.jpg',
  c: demoDiaryUpload,
}

// ——— Lotte: webshop én salon, met abonnement en kuur ———
const lotte: Account = {
  customer: baseCustomer({
    id: 'klant-2291',
    firstName: 'Lotte',
    lastName: 'de Vries',
    email: 'lotte@voorbeeld.nl',
    phone: '06 12 34 56 78',
    address: { street: 'Javastraat', houseNumber: '41-2', postcode: '1094 HA', city: 'Amsterdam', country: 'NL' },
    payment: { method: 'ideal', label: 'Wero · ING' },
    prefs: { refillWhatsApp: true, refillEmail: false, newsletter: true },
    memberSince: day(-120),
    acquisitionSource: { channel: 'instagram', at: day(-120), via: 'bestelling' },
  }),
  plan: {
    routineId: 'pigment-protocol',
    startedOn: day(-49),
    focus: 'Pigment op de wangen, na twee zomers zonder SPF',
    note: 'Retinal nog rustig opbouwen. Na je volgende sessie kijken we of je naar vier avonden kunt.',
    items: [
      { productId: 'emerginc-kombucha-cleanser', slots: ['ochtend', 'avond'], instruction: 'Een hazelnoot op vochtige huid, 30 seconden masseren.', startedOn: day(-49), lastsWeeks: 12 },
      { productId: 'spirulina-toner', slots: ['ochtend', 'avond'], instruction: 'Op een wattenschijfje, of in je handen en zacht indrukken.', startedOn: day(-49), lastsWeeks: 14 },
      { productId: 'emerginc-vitamine-c-serum', slots: ['ochtend'], instruction: 'Drie druppels op een droge huid, ook in je hals.', startedOn: day(-52), lastsWeeks: 8 },
      { productId: 'medik8-chrystal-retinal-1', slots: ['avond'], instruction: 'Een erwt, niet rond je ogen. Prikt het? Sla een avond over.', frequency: '2–3 avonden per week', startedOn: day(-20), lastsWeeks: 16 },
      { productId: 'total-moisture-daily-facial-cream', slots: ['avond'], instruction: 'Als laatste stap, ook op de avonden zonder retinal.', startedOn: day(-49), lastsWeeks: 8 },
      { productId: 'emerginc-sun-spf-50-tinted', slots: ['ochtend'], instruction: 'Twee vingerlengtes. Ook als het bewolkt is.', startedOn: day(-30), lastsWeeks: 6 },
    ],
  },
  subscription: {
    id: 'ab-0481',
    status: 'actief',
    intervalWeeks: 8,
    nextDelivery: day(9),
    items: [
      { productId: 'emerginc-kombucha-cleanser', qty: 1 },
      { productId: 'emerginc-vitamine-c-serum', qty: 1 },
      { productId: 'emerginc-sun-spf-50-tinted', qty: 1 },
    ],
    extras: [],
    deliveriesDone: 2,
  },
  appointments: [
    { id: 'af-3012', treatmentSlug: 'goddess-lift', date: day(5), time: '10:30', kind: 'salon', status: 'bevestigd', session: 3 },
    { id: 'af-3020', treatmentSlug: 'shelfie-time', date: day(26), time: '19:30', kind: 'video', status: 'bevestigd' },
  ],
  visits: [
    { treatmentSlug: 'goddess-lift', date: day(-16) },
    { treatmentSlug: 'goddess-lift', date: day(-37) },
    { treatmentSlug: 'vitamine-c-behandeling', date: day(-52) },
    { treatmentSlug: 'shelfie-time', date: day(-118) },
  ],
  // Vooruit betaald aan de kassa, vóór de eerste sessie (docs/KASSA-ABONNEMENTEN.md §4).
  course: { treatmentSlug: 'goddess-lift', total: 6, done: 2, prepaid: { paid: 635, paidOn: day(-40), validUntil: shiftMonths(day(-40), 24) } },
  orders: [
    { id: 'BB-2026-0412', date: day(-3), lines: [{ productId: 'vitality-mask', qty: 1, price: 0 }], shipping: 5.95, status: 'Onderweg', viaSubscription: false },
    { id: 'BB-2026-0398', date: day(-30), lines: [{ productId: 'emerginc-sun-spf-50-tinted', qty: 1, price: 0 }], shipping: 5.95, status: 'Bezorgd', viaSubscription: false },
    { id: 'BB-2026-0371', date: day(-20), lines: [{ productId: 'medik8-chrystal-retinal-1', qty: 1, price: 0 }], shipping: 5.95, status: 'Bezorgd', viaSubscription: false },
    {
      id: 'BB-2026-0322',
      date: day(-49),
      lines: [
        { productId: 'emerginc-kombucha-cleanser', qty: 1, price: 0 },
        { productId: 'emerginc-vitamine-c-serum', qty: 1, price: 0 },
        { productId: 'emerginc-sun-spf-50-tinted', qty: 1, price: 0 },
      ],
      shipping: 0,
      status: 'Bezorgd',
      viaSubscription: true,
    },
    {
      id: 'BB-2026-0301',
      date: day(-52),
      lines: [
        { productId: 'spirulina-toner', qty: 1, price: 0 },
        { productId: 'total-moisture-daily-facial-cream', qty: 1, price: 0 },
      ],
      shipping: 5.95,
      status: 'Bezorgd',
      viaSubscription: false,
    },
    {
      id: 'BB-2026-0244',
      date: day(-105),
      lines: [
        { productId: 'emerginc-kombucha-cleanser', qty: 1, price: 0 },
        { productId: 'emerginc-vitamine-c-serum', qty: 1, price: 0 },
      ],
      shipping: 0,
      status: 'Bezorgd',
      viaSubscription: true,
    },
  ],
  diary: [
    { id: 'foto-1', date: day(-49), image: photo.a, note: 'Start. De vlekken op mijn linkerwang vallen me het meest op.' },
    { id: 'foto-2', date: day(-21), image: photo.b, note: 'Voelt zachter. Vlekken iets lichter, denk ik?' },
  ],
}

// ——— Mirjam: komt alleen voor de salon, neemt verder niets af ———
const mirjam: Account = {
  customer: baseCustomer({ id: 'klant-1874', firstName: 'Mirjam', lastName: 'Bakker', email: 'mirjam@voorbeeld.nl', phone: '06 98 76 54 32', memberSince: day(-240) }),
  plan: emptyPlan(day(-240)),
  subscription: null,
  // Morgen: binnen de termijn, dus alleen nog via WhatsApp te verzetten.
  appointments: [{ id: 'af-2988', treatmentSlug: 'goddess-lift', date: day(1), time: '14:00', kind: 'salon', status: 'bevestigd', session: 5 }],
  visits: [
    { treatmentSlug: 'goddess-lift', date: day(-20) },
    { treatmentSlug: 'goddess-lift', date: day(-41) },
    { treatmentSlug: 'goddess-lift', date: day(-63) },
    { treatmentSlug: 'goddess-lift', date: day(-84) },
    { treatmentSlug: 'persian-delight', date: day(-150) },
    { treatmentSlug: 'ibiza-behandeling', date: day(-240) },
  ],
  course: { treatmentSlug: 'goddess-lift', total: 6, done: 4 },
  orders: [],
  diary: [],
}

// ——— Noor: net voor het eerst besteld (pakket onderweg), geen abonnement, nog nooit in de salon ———
const noor: Account = {
  customer: baseCustomer({
    id: 'klant-2317',
    firstName: 'Noor',
    lastName: 'El Amrani',
    email: 'noor@voorbeeld.nl',
    address: { street: 'Oudegracht', houseNumber: '112', postcode: '3511 AX', city: 'Utrecht', country: 'NL' },
    memberSince: day(-6),
  }),
  plan: emptyPlan(day(-6)),
  subscription: null,
  appointments: [],
  visits: [],
  course: null,
  orders: [
    {
      id: 'BB-2026-0419',
      date: day(-2),
      lines: [
        { productId: 'emerginc-pomegranate-gel-cleanser', qty: 1, price: 0 },
        { productId: 'medik8-hydr8-b5', qty: 1, price: 0 },
        { productId: 'emerginc-sun-spf-50-tinted', qty: 1, price: 0 },
      ],
      shipping: 0,
      status: 'Onderweg',
      tracking: { carrier: 'PostNL', code: '3SBBWU0419NL' },
      viaSubscription: false,
    },
  ],
  diary: [],
}

// ——— Sophie: eerste aanvraag, nog nooit geweest ———
const sophie: Account = {
  customer: baseCustomer({ id: 'klant-2324', firstName: 'Sophie', lastName: 'Vermeulen', email: 'sophie@voorbeeld.nl', phone: '06 22 45 81 09', memberSince: day(-1) }),
  plan: emptyPlan(day(-1)),
  subscription: null,
  appointments: [{ id: 'af-3031', treatmentSlug: 'bindweefselmassage-los', date: day(3), time: '10:30', kind: 'salon', status: 'aanvraag', request: 'nieuw' }],
  visits: [],
  course: null,
  orders: [],
  diary: [],
}

// ——— Iris: thuis én salon, abonnement gepauzeerd, wil haar videocall verzetten ———
const iris: Account = {
  customer: baseCustomer({
    id: 'klant-2103',
    firstName: 'Iris',
    lastName: 'Mulder',
    email: 'iris@voorbeeld.nl',
    phone: '06 41 17 30 88',
    address: { street: 'Wittevrouwenstraat', houseNumber: '8', postcode: '3512 CT', city: 'Utrecht', country: 'NL' },
    payment: { method: 'klarna', label: 'Klarna' },
    memberSince: day(-300),
    acquisitionSource: { channel: 'via-iemand', at: day(-300), via: 'afspraak' },
  }),
  plan: {
    routineId: 'compleet-anti-ageing',
    startedOn: day(-140),
    focus: 'Eerste lijntjes en een vermoeide teint',
    note: 'Retinoate twee keer per week is genoeg voor jou. Na de zomer bouwen we op.',
    items: [
      { productId: 'emerginc-pomegranate-gel-cleanser', slots: ['ochtend', 'avond'], instruction: 'Kort masseren, lauw afspoelen.', startedOn: day(-60), lastsWeeks: 12 },
      { productId: 'medik8-retinoate-bestseller', slots: ['avond'], instruction: 'Een erwt, na het reinigen op een droge huid.', frequency: '2 avonden per week', startedOn: day(-60), lastsWeeks: 16 },
      { productId: 'medik8-hydr8-b5', slots: ['ochtend', 'avond'], instruction: 'Twee druppels, zacht indrukken.', startedOn: day(-50), lastsWeeks: 8 },
      { productId: 'emerginc-sun-spf-50-tinted', slots: ['ochtend'], instruction: 'Twee vingerlengtes, elke ochtend.', startedOn: day(-40), lastsWeeks: 6 },
    ],
  },
  subscription: {
    id: 'ab-0377',
    status: 'gepauzeerd',
    intervalWeeks: 8,
    nextDelivery: day(24),
    pausedUntil: day(21),
    items: [
      { productId: 'medik8-hydr8-b5', qty: 1 },
      { productId: 'emerginc-sun-spf-50-tinted', qty: 1 },
    ],
    extras: [],
    deliveriesDone: 4,
  },
  appointments: [{ id: 'af-3025', treatmentSlug: 'shelfie-time', date: day(6), time: '19:00', kind: 'video', status: 'aanvraag', request: 'verzet' }],
  visits: [
    { treatmentSlug: 'vitamine-c-behandeling', date: day(-70) },
    { treatmentSlug: 'shelfie-time', date: day(-140) },
  ],
  course: null,
  orders: [
    {
      id: 'BB-2026-0356',
      date: day(-40),
      lines: [
        { productId: 'medik8-hydr8-b5', qty: 1, price: 0 },
        { productId: 'emerginc-sun-spf-50-tinted', qty: 1, price: 0 },
      ],
      shipping: 0,
      status: 'Bezorgd',
      viaSubscription: true,
    },
    {
      id: 'BB-2026-0330',
      date: day(-60),
      lines: [
        { productId: 'emerginc-pomegranate-gel-cleanser', qty: 1, price: 0 },
        { productId: 'medik8-retinoate-bestseller', qty: 1, price: 0 },
      ],
      shipping: 0,
      status: 'Bezorgd',
      viaSubscription: false,
    },
  ],
  diary: [],
}

// ——— Eva: alleen webshop, bestelt trouw, nog geen persoonlijk huidplan ———
const eva: Account = {
  customer: baseCustomer({
    id: 'klant-2208',
    firstName: 'Eva',
    lastName: 'Jansen',
    email: 'eva@voorbeeld.nl',
    phone: '06 55 90 12 37',
    address: { street: 'Kruisweg', houseNumber: '64', postcode: '2011 LE', city: 'Haarlem', country: 'NL' },
    memberSince: day(-90),
  }),
  plan: emptyPlan(day(-90)),
  subscription: {
    id: 'ab-0466',
    status: 'actief',
    intervalWeeks: 6,
    nextDelivery: day(2),
    items: [
      { productId: 'emerginc-peach-toner', qty: 1 },
      { productId: 'emerginc-kombucha-cleanser', qty: 1 },
    ],
    extras: [],
    deliveriesDone: 1,
    // Voorbeeld van F20: de automatische betaling voor de volgende levering lukte niet.
    // De order staat in seed/inbox.ts (BB-2026-0436, betaling 'mislukt').
    paymentIssue: { orderId: 'BB-2026-0436', token: 'demo', deliveryDate: day(2), reason: 'mislukt' },
  },
  appointments: [],
  visits: [],
  course: null,
  orders: [
    {
      id: 'BB-2026-0420',
      date: day(0),
      lines: [
        { productId: 'emerginc-peach-toner', qty: 2, price: 0 },
        { productId: 'emerginc-kombucha-cleanser', qty: 1, price: 0 },
      ],
      shipping: 0,
      status: 'In behandeling',
      viaSubscription: false,
    },
    { id: 'BB-2026-0288', date: day(-90), lines: [{ productId: 'emerginc-kombucha-cleanser', qty: 1, price: 0 }], shipping: 5.95, status: 'Bezorgd', viaSubscription: false },
  ],
  diary: [],
}

// ——— Fleur: salon, kuur net afgerond, deelt haar foto's graag ———
const fleur: Account = {
  customer: baseCustomer({ id: 'klant-1650', firstName: 'Fleur', lastName: 'Kok', email: 'fleur@voorbeeld.nl', phone: '06 30 72 14 56', diaryConsent: true, memberSince: day(-420) }),
  plan: emptyPlan(day(-420)),
  subscription: null,
  appointments: [],
  visits: [-4, -25, -46, -67, -88, -109].map((d) => ({ treatmentSlug: 'goddess-lift', date: day(d) })),
  course: { treatmentSlug: 'goddess-lift', total: 6, done: 6 },
  orders: [],
  diary: [
    { id: 'foto-f1', date: day(-109), image: photo.a, note: 'Voor de eerste sessie.', marketing: true },
    { id: 'foto-f2', date: day(-60), image: photo.b, note: 'Halverwege. Kaaklijn voelt strakker.' },
    { id: 'foto-f3', date: day(-4), image: photo.c, note: 'Laatste sessie gehad!' },
  ],
}

// ——— Nadia: één bestelling, al lang niet meer in de salon ———
const nadia: Account = {
  customer: baseCustomer({
    id: 'klant-1932',
    firstName: 'Nadia',
    lastName: 'Bakker',
    email: 'nadia@voorbeeld.nl',
    address: { street: 'Czaar Peterstraat', houseNumber: '20', postcode: '1018 PR', city: 'Amsterdam', country: 'NL' },
    memberSince: day(-200),
  }),
  plan: emptyPlan(day(-200)),
  subscription: null,
  appointments: [],
  visits: [{ treatmentSlug: 'ibiza-behandeling', date: day(-180) }],
  course: null,
  orders: [{ id: 'BB-2026-0415', date: day(-1), lines: [{ productId: 'medik8-retinoate-bestseller', qty: 1, price: 0 }], shipping: 5.95, status: 'In behandeling', viaSubscription: false }],
  diary: [],
}

/** Alle demoklanten, in de volgorde van het inlogscherm. */
export const demoCustomers: Account[] = [lotte, mirjam, noor, sophie, iris, eva, fleur, nadia].map(withPaidPrices)

/** Korte uitleg per klant voor het inlogscherm van de demo. */
export const demoLoginHints: Record<string, string> = {
  'klant-2291': 'Webshop, abonnement en kuur',
  'klant-1874': 'Alleen de salon, midden in een kuur',
  'klant-2317': 'Net voor het eerst besteld',
  'klant-2324': 'Eerste afspraak aangevraagd',
  'klant-2103': 'Abonnement gepauzeerd, wil verzetten',
  'klant-2208': 'Abonnementsbetaling mislukt, nog geen huidplan',
  'klant-1650': 'Kuur afgerond, deelt foto’s',
  'klant-1932': 'Lang niet in de salon geweest',
}

/** Interne notities van Sarah — nooit zichtbaar voor de klant. */
export const demoNotes: Record<string, string> = {
  'klant-2291': 'Gevoelig voor parfum. Houdt van uitleg waarom iets werkt.',
  'klant-2208': 'Is zwanger (12 weken, zie bestelling). Geen retinoïden adviseren.',
  'klant-1650': 'Wil na de kuur graag onderhoud, elke 6 weken. Nog inplannen.',
}
